import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { ArrowLeft, Save } from "lucide-react"
import { Link, useNavigate, useParams } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { VeiculoFormSections } from "@/components/vehicle/VeiculoFormSections"
import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/integrations/supabase/client"
import type { TablesUpdate } from "@/integrations/supabase/types"
import { getPublicErrorMessage } from "@/lib/errors"
import { buscarUltimoRegistroFIPE, salvarHistoricoFIPE, validarPlaca, type OrigemFIPE } from "@/lib/fipeService"
import { formatarMoedaInput, parsearMoeda } from "@/lib/utils"
import { normalizarCombustivel, normalizarPlaca } from "@/lib/veiculoUtils"
import {
  DOCUMENT_BUCKET,
  PHOTO_BUCKET,
  createSignedFileUrl,
  getUploadStoragePath,
  uploadFileToBucket,
  type UploadableDocument,
  type UploadablePhoto,
} from "@/lib/vehicleStorage"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { toast } from "sonner"

interface VehicleFormData {
  placa: string
  marca: string
  modelo: string
  versao?: string | null
  ano: string
  anoModelo?: string | null
  cor: string | null
  combustivel: string | null
  cambio: string | null
  quilometragem: string | null
  motor: string | null
  potencia?: string | null
  capacidadeCarga?: string | null
  passageiros?: string | null
  tipoVeiculo?: string | null
  cidade?: string | null
  estado?: string | null
  situacaoVeiculo?: string | null
  portas: string | null
  observacoes: string | null
  chassi?: string | null
  renavam?: string | null
  foto_principal?: string | null
  valorFIPE?: string | null
}

const EditarVeiculo = () => {
  const { id } = useParams<{ id: string }>()
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<VehicleFormData>()
  const { loja } = useAuth()
  const vertical = useTipoOperacao()
  const mostrarValorFIPE = vertical.documentos.mostrarValoresFinanceiros
  const navigate = useNavigate()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [documents, setDocuments] = useState<UploadableDocument[]>([])
  const [fotoPrincipal, setFotoPrincipal] = useState<UploadablePhoto | string | null>(null)
  const [ultimoValorFIPE, setUltimoValorFIPE] = useState<number | null>(null)
  const [fipeOrigem, setFipeOrigem] = useState<OrigemFIPE | null>(null)
  const [brandOpen, setBrandOpen] = useState(false)
  const [brandSearchValue, setBrandSearchValue] = useState("")

  useEffect(() => {
    if (id) {
      fetchVehicleData()
      fetchDocumentosVeiculo()
    }
  }, [id])

  const fetchVehicleData = async () => {
    if (!id) return

    try {
      const { data, error } = await supabase
        .from("veiculos")
        .select("*")
        .eq("id", id)
        .single()

      if (error) {
        toast.error("Erro ao carregar dados do veículo")
        navigate("/veiculos")
        return
      }

      const fotoPrincipalUrl = await createSignedFileUrl(PHOTO_BUCKET, data.foto_principal)
      setFotoPrincipal(data.foto_principal ? {
        url: fotoPrincipalUrl ?? data.foto_principal,
        storagePath: data.foto_principal,
      } : null)

      const ultimoFIPE = mostrarValorFIPE ? await buscarUltimoRegistroFIPE(id) : null
      setUltimoValorFIPE(ultimoFIPE?.valor_fipe ?? null)
      setFipeOrigem(ultimoFIPE?.origem ?? null)

      reset({
        placa: data.placa || "",
        marca: data.marca || "",
        modelo: data.modelo || "",
        versao: data.versao || "",
        ano: data.ano ? data.ano.toString() : "",
        anoModelo: data.anomodelo || "",
        cor: data.cor || "",
        combustivel: data.combustivel || "",
        cambio: data.cambio || "",
        quilometragem: data.quilometragem ? data.quilometragem.toString() : "",
        motor: data.motor || "",
        potencia: data.potencia || "",
        capacidadeCarga: data.capacidadeCarga ? data.capacidadeCarga.toString() : "",
        passageiros: data.passageiros ? data.passageiros.toString() : "",
        tipoVeiculo: data.tipoVeiculo || "",
        cidade: data.cidade || "",
        estado: data.estado || "",
        situacaoVeiculo: data.situacaoVeiculo || "",
        portas: data.portas ? data.portas.toString() : "",
        observacoes: data.observacoes || "",
        chassi: data.chassi || "",
        renavam: data.renavam || "",
        foto_principal: data.foto_principal || "",
        valorFIPE: ultimoFIPE?.valor_fipe ? formatarMoedaInput(ultimoFIPE.valor_fipe) : "",
      })
    } catch (error) {
      toast.error("Erro inesperado ao carregar dados")
      console.error("Erro:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDocumentosVeiculo = async () => {
    if (!id) return

    try {
      const { data, error } = await supabase
        .from("documentos_veiculo")
        .select("id, tipo_documento, url, nome_arquivo")
        .eq("veiculo_id", id)
        .order("created_at", { ascending: false })

      if (error) {
        console.error("Erro ao buscar documentos do veículo:", error)
        return
      }

      const documentosResolvidos = await Promise.all((data || []).map(async (documento) => ({
        ...documento,
        url: (await createSignedFileUrl(DOCUMENT_BUCKET, documento.url)) ?? documento.url,
        storagePath: documento.url,
      })))

      setDocuments(documentosResolvidos)
    } catch (error) {
      console.error("Erro inesperado ao buscar documentos do veículo:", error)
    }
  }

  const onSubmit = async (data: VehicleFormData) => {
    try {
      const placaNormalizada = normalizarPlaca(data.placa)
      if (!validarPlaca(placaNormalizada)) {
        toast.error("Placa inválida. Use o formato AAA0A00 ou AAA0000.")
        return
      }

      const combustivelNormalizado = normalizarCombustivel(data.combustivel)
      if (data.combustivel && !combustivelNormalizado) {
        toast.error("Combustível inválido. Use gasolina, etanol, flex, diesel, gnv, eletrico ou hibrido.")
        return
      }

      const valorFIPEInformado = mostrarValorFIPE ? parsearMoeda(data.valorFIPE || "") : 0
      if (mostrarValorFIPE && data.valorFIPE?.trim() && valorFIPEInformado <= 0) {
        toast.error("Informe um valor FIPE válido ou deixe o campo vazio.")
        return
      }

      setIsSubmitting(true)
      if (!id || !loja?.id) {
        toast.error("Veículo ou loja não encontrados")
        return
      }

      let fotoPrincipalPath: string | null = null

      if (typeof fotoPrincipal === "string") {
        fotoPrincipalPath = fotoPrincipal || null
      } else if (fotoPrincipal?.file) {
        const { path } = await uploadFileToBucket(
          PHOTO_BUCKET,
          fotoPrincipal.file,
          `lojas/${loja.id}/veiculos/${id}/capa`,
        )
        fotoPrincipalPath = path
      } else if (fotoPrincipal) {
        fotoPrincipalPath = getUploadStoragePath(fotoPrincipal) ?? null
      }

      const updateData: TablesUpdate<"veiculos"> = {
        placa: placaNormalizada,
        marca: data.marca,
        modelo: data.modelo,
        versao: data.versao || null,
        ano: parseInt(data.ano),
        anomodelo: data.anoModelo || null,
        cor: data.cor || null,
        combustivel: combustivelNormalizado,
        cambio: data.cambio === "automatica" ? "automatico" : data.cambio || null,
        quilometragem: data.quilometragem ? parseInt(data.quilometragem) : null,
        motor: data.motor || null,
        potencia: data.potencia || null,
        capacidadecarga: data.capacidadeCarga ? parseInt(data.capacidadeCarga) : null,
        passageiros: data.passageiros ? parseInt(data.passageiros) : null,
        tipoveiculo: data.tipoVeiculo || null,
        cidade: data.cidade || null,
        estado: data.estado || null,
        situacaoveiculo: data.situacaoVeiculo || null,
        portas: data.portas ? parseInt(data.portas) : null,
        observacoes: data.observacoes || null,
        chassi: data.chassi || null,
        renavam: data.renavam || null,
        foto_principal: fotoPrincipalPath,
      }

      const { error } = await supabase
        .from("veiculos")
        .update(updateData)
        .eq("id", id)

      if (error) {
        console.error("Erro detalhado do Supabase:", error)
        toast.error(getPublicErrorMessage(error.message, "Não foi possível atualizar o veículo agora."))
        return
      }

      const documentosPreparados = await Promise.all(
        documents.map(async (documento) => {
          if (documento.file) {
            const { path, signedUrl } = await uploadFileToBucket(
              DOCUMENT_BUCKET,
              documento.file,
              `lojas/${loja.id}/veiculos/${id}/documentos`,
            )

            return {
              ...documento,
              url: signedUrl,
              storagePath: path,
            }
          }

          return documento
        }),
      )

      const { error: deleteDocumentosError } = await supabase
        .from("documentos_veiculo")
        .delete()
        .eq("veiculo_id", id)

      if (deleteDocumentosError) {
        console.error("Erro ao remover documentos antigos:", deleteDocumentosError)
        toast.error("Erro ao atualizar documentos do veículo")
        return
      }

      if (documentosPreparados.length > 0) {
        const { error: insertDocumentosError } = await supabase
          .from("documentos_veiculo")
          .insert(documentosPreparados.map((documento) => ({
            veiculo_id: id,
            tipo_documento: documento.tipo_documento,
            url: documento.storagePath ?? documento.url,
            nome_arquivo: documento.nome_arquivo,
          })))

        if (insertDocumentosError) {
          console.error("Erro ao inserir documentos:", insertDocumentosError)
          toast.error("Erro ao atualizar documentos do veículo")
          return
        }
      }

      if (mostrarValorFIPE && valorFIPEInformado > 0 && Math.abs(valorFIPEInformado - (ultimoValorFIPE ?? 0)) >= 0.01) {
        await salvarHistoricoFIPE(id, valorFIPEInformado, placaNormalizada, "manual")
      }

      toast.success("Veículo atualizado com sucesso!")
      navigate(`/veiculo/${id}`)
    } catch (error) {
      console.error("Erro inesperado ao atualizar veículo:", error)
      toast.error("Erro inesperado ao atualizar veículo")
    } finally {
      setIsSubmitting(false)
    }
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i)

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/veiculo/${id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Editar Veículo</h1>
          <p className="text-muted-foreground">Atualize as informações do veículo</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <VeiculoFormSections
          register={register}
          control={control}
          errors={errors}
          years={years}
          documents={documents}
          onDocumentsUpdate={setDocuments}
          photo={fotoPrincipal}
          onPhotoUpdate={setFotoPrincipal}
          disabled={isSubmitting}
          brandOpen={brandOpen}
          setBrandOpen={setBrandOpen}
          brandSearchValue={brandSearchValue}
          setBrandSearchValue={setBrandSearchValue}
          showValorFIPE={mostrarValorFIPE}
          valorFIPEHelperText={fipeOrigem === "api"
            ? "Último valor automático. Edite para registrar uma FIPE manual."
            : "Informe ou atualize manualmente quando necessário."}
        />

        <div className="flex gap-4 justify-end">
          <Button variant="outline" asChild>
            <Link to={`/veiculo/${id}`}>Cancelar</Link>
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default EditarVeiculo
